// ProfileLivestreamItem.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { format, fromUnixTime, addSeconds } from 'date-fns';
import { withTranslation } from 'react-i18next';

import { color, textColor } from '../style/variables.js';

import numberWithCommas from '../resource/numberWithCommas.js';
import { getTimeFormat } from '../resource/playerUtils.js';
import { ProfileListType } from '../resource/profileConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import LiveIconSource from '../../img/btn_live_report.svg';
import DiamondIconSource from '../../img/ic_diamond.svg';

export const ProfileLivestreamItem = ({
  sessionId,
  startUnixTimestamp,
  endUnixTimestamp,
  summary,
  t,
}) => {
  const isDurationProcessing = !startUnixTimestamp || !endUnixTimestamp;
  const duration = isDurationProcessing
    ? t('general_processing', {
        ns: TranslationNamespace.PROFILE,
      })
    : format(
        addSeconds(new Date(0), endUnixTimestamp - startUnixTimestamp),
        getTimeFormat(endUnixTimestamp - startUnixTimestamp)
      );
  return (
    <StyledProfileTabLivestreamItem
      to={`/me/${ProfileListType.LIVESTREAM_SESSION}/${sessionId}`}
    >
      <LivestreamIcon>
        <img src={LiveIconSource} />
      </LivestreamIcon>
      <StartTime>
        {startUnixTimestamp
          ? format(fromUnixTime(startUnixTimestamp), 'yyyy/MM/dd HH:mm')
          : '-'}
      </StartTime>
      <DurationTitle>
        {t('title_stream_duration', {
          ns: TranslationNamespace.PROFILE,
        })}
      </DurationTitle>
      <Duration isDurationProcessing={isDurationProcessing}>
        {duration}
      </Duration>
      <DiamondTitle>
        {t('title_stream_total_diamond', {
          ns: TranslationNamespace.PROFILE,
        })}
      </DiamondTitle>
      <Diamond>
        <img src={DiamondIconSource} />
        {numberWithCommas({ number: summary['total.diamonds'] || 0 })}
      </Diamond>
    </StyledProfileTabLivestreamItem>
  );
};

ProfileLivestreamItem.propTypes = {
  sessionId: PropTypes.string,
  startUnixTimestamp: PropTypes.number,
  endUnixTimestamp: PropTypes.number,
  summary: PropTypes.object,
  t: PropTypes.func.isRequired,
};

ProfileLivestreamItem.defaultProps = {
  sessionId: null,
  startUnixTimestamp: null,
  endUnixTimestamp: null,
  summary: {},
};

const StyledProfileTabLivestreamItem = styled(Link)`
  width: 100%;
  height: 88px;
  display: grid;
  grid-template-areas:
    'icon start-time start-time'
    'icon duration-title duration'
    'icon diamond-title diamond';
  grid-template-columns: 60px auto auto;
  grid-template-rows: 14px 12px 16px;
  grid-column-gap: 12px;
  grid-row-gap: 8px;
  align-content: center;
  & + & {
    border-top: 1px solid rgba(100, 100, 100, 0.08);
  }
`;

const LivestreamIcon = styled.div`
  border-radius: 6px;
  width: 60px;
  height: 60px;
  grid-area: icon;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.grey['900']};
  > img {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }
`;

const StartTime = styled.div`
  grid-area: start-time;
  font-size: 14px;
  line-height: 1;
  color: ${textColor.white};
`;

const DurationTitle = styled.div`
  grid-area: duration-title;
  font-size: 12px;
  line-height: 1;
  color: ${textColor.black};
`;

const DiamondTitle = styled.div`
  grid-area: diamond-title;
  font-size: 12px;
  line-height: 1;
  color: ${textColor.black};
`;

const Duration = styled.div.attrs(({ isDurationProcessing }) => ({
  style: {
    color: isDurationProcessing ? 'rgba(200, 200, 200, 0.5)' : textColor.white,
  },
}))`
  grid-area: duration;
  font-size: 12px;
  line-height: 1;
  text-align: right;
`;

const Diamond = styled.div`
  grid-area: diamond;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 12px;
  line-height: 1;
  color: ${textColor.primary};
  > img {
    width: 16px;
    height: 16px;
    margin-right: 4px;
  }
`;

export default withTranslation()(ProfileLivestreamItem);
